import { useEffect, useRef } from "react";
import type { DefaultValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { useGetRreTinValidationsQuery } from "services/rre-api";

import { useSelector, useDispatch } from "utils/redux";

import { setValidationState } from "reducers/main";
import type { TinType } from "types/DataTypes";
import RRETinBasicInfoSection from "./rre-tin-sections/RRETinBasicInfoSection";
import RRETinMailingAddressSection from "./rre-tin-sections/RRETinMailingAddressSection";
import RRETinForeignAddressSection from "./rre-tin-sections/RRETinForeignAddressSection";
import RRETinRecoveryAgentSection from "./rre-tin-sections/RRETinRecoveryAgentSection";
import RRETinAdditionalInfoSection from "./rre-tin-sections/RRETinAdditionalInfoSection";

type RRETinFormProps = {
  rreTin: TinType;
  onSave: (field: string, value: unknown) => Promise<void>;
};

const RRETinForm = ({ rreTin, onSave }: RRETinFormProps) => {
  const dispatch = useDispatch();
  const { rreTinId } = useParams<{ rreTinId?: string }>();
  const prevRreTinIdRef = useRef(rreTin.id);

  const defaultValues: DefaultValues<TinType> = {
    ...rreTin,
  };

  const { control, getValues, reset, setValue, formState } = useForm<TinType>({
    defaultValues,
  });

  const { data: rreTinValidation } = useGetRreTinValidationsQuery(
    { id: rreTinId },
    { skip: !rreTinId },
  );

  const constants = useSelector((state) => state.shared.constants);

  useEffect(() => {
    // track the current TIN ID so we can use keepDirtyValues to prevent form reset onChange
    // if the user is switching TINs, that's when we want to reset the form with the new TIN data
    const isSwitchingTins = prevRreTinIdRef.current !== rreTin.id;
    prevRreTinIdRef.current = rreTin.id;

    reset(rreTin, {
      keepDirtyValues: !isSwitchingTins,
    });
  }, [rreTin, reset, formState.dirtyFields]);

  useEffect(() => {
    if (rreTinValidation) {
      const validationData = Object.values(rreTinValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreTinValidation]);

  const handleFieldChange = async (field: string | null, value: unknown) => {
    if (field) {
      await onSave(field, value);
    } else if (typeof value === "object" && value !== null) {
      // handle multiple fields at once
      for (const [key, val] of Object.entries(value)) {
        await onSave(key, val);
      }
    }
  };

  if (!rreTin) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  return (
    <form className="overview" onKeyDown={handleKeyDown} autoComplete="off">
      <RRETinBasicInfoSection
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />

      <RRETinMailingAddressSection
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />

      <RRETinAdditionalInfoSection
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />

      <RRETinForeignAddressSection
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />

      <RRETinRecoveryAgentSection
        control={control}
        getValues={getValues}
        setValue={setValue}
        handleFieldChange={handleFieldChange}
        constants={constants}
      />
    </form>
  );
};

export default RRETinForm;
